import { translateIn, SUPPORTED_LANGUAGES, getCurrentLanguage } from './index';
import type { Language, TranslationKey } from './translations';
import type { DeviceModel } from '../api/deviceModels';

type TFn = (key: TranslationKey, vars?: Record<string, string | number>) => string;
type TpFn = (base: string, n: number) => string;

/**
 * Noms par défaut que l'application donne aux éléments d'architecture (zones, tableaux, compteurs, points).
 * Ils sont enregistrés en base dans la langue de création (en pratique le français) et relus tels quels :
 * à l'affichage, un nom resté identique à un nom par défaut est montré dans la langue de l'interface.
 * Un nom personnalisé par l'utilisateur n'est jamais modifié.
 */
const DEFAULT_NAME_KEYS: TranslationKey[] = [
  // zones (boîte à outils et éditeur)
  'arch2_zone_wattel', 'arch2_zone_lighting', 'arch2_zone_heating', 'arch2_zone_ac', 'arch2_zone_sockets',
  'arch2_zone_office', 'arch2_zone_hvac', 'arch2_zone_kitchen', 'arch2_zone_server',
  'arch2_zone_workshop', 'arch2_zone_cleanroom', 'arch2_zone_parking',
  // zones des modèles d'installation
  'arch2_tpl_z_living', 'arch2_tpl_z_kitchen', 'arch2_tpl_z_bedroom', 'arch2_tpl_z_garage', 'arch2_tpl_z_bedrooms',
  'arch2_tpl_z_bathroom', 'arch2_tpl_z_outdoor', 'arch2_tpl_z_offices', 'arch2_tpl_z_server_room', 'arch2_tpl_z_hvac',
  'arch2_tpl_z_lighting', 'arch2_tpl_z_window', 'arch2_tpl_z_sales', 'arch2_tpl_z_storeroom', 'arch2_tpl_z_ac',
  // compteurs
  'arch2_meter_linky', 'arch2_meter_edf', 'arch2_meter_three_phase', 'arch2_submeter', 'arch2_meter_production',
  'arch2_linky_single', 'arch2_linky_three',
  // tableaux à nom fixe
  'arch2_electrical_panel', 'arch2_subpanel', 'arch2_outdoor_box', 'arch2_panel_1breaker', 'arch2_secondary_panel',
  // points de charge / prises
  'arch2_socket_16a', 'arch2_socket_32a', 'arch2_point_misc',
];

/** Clé du nom par défaut correspondant à `label`, quelle que soit la langue dans laquelle il a été enregistré. */
function defaultKeyOf(label: string): TranslationKey | undefined {
  const name = label.trim();
  return DEFAULT_NAME_KEYS.find(key => SUPPORTED_LANGUAGES.some(lang => translateIn(lang, key) === name));
}

// Modèles d'appareils du catalogue (table zos_device_models), tenus à jour par useDeviceModels.
let knownDeviceModels: DeviceModel[] = [];
export function setKnownDeviceModels(models: DeviceModel[]): void { knownDeviceModels = models; }

/** Nom d'un modèle d'appareil dans la langue de l'interface (traduction facultative, sinon le nom principal). */
export function deviceModelName(model: Pick<DeviceModel, 'name' | 'nameEn' | 'nameHi'>, lang: Language = getCurrentLanguage()): string {
  if (lang === 'en') return model.nameEn || model.name;
  if (lang === 'hi') return model.nameHi || model.nameEn || model.name;
  return model.name;
}

/** Modèle dont `label` est le nom (actuel, traduit ou ancien) : un appareil resté au nom de son modèle. */
function deviceModelOf(label: string): DeviceModel | undefined {
  const name = label.trim();
  return knownDeviceModels.find(m => [m.name, m.nameEn, m.nameHi, ...m.formerNames].includes(name));
}

/** Nom affiché d'un élément d'architecture : nom par défaut traduit, sinon le nom enregistré tel quel. */
export function displayDefaultName(label: string | undefined | null, t: TFn, tp: TpFn): string {
  if (!label) return label ?? '';
  const model = deviceModelOf(label);
  if (model) return deviceModelName(model);
  const key = defaultKeyOf(label);
  if (key) return t(key);
  // tableaux nommés d'après leur nombre de rangées (« Tableau 2 rangées », « Tableau Principal 3R »…)
  let m = /^Tableau (\d+) rangées?$/.exec(label);
  if (m) return tp('arch2_panel_rows', Number(m[1]));
  m = /^(Drivia|Gamma) (\d+) rangées$/.exec(label);
  if (m) return t('arch2_brand_rows', { brand: m[1], n: m[2] });
  m = /^Tableau Principal (\d+)R$/.exec(label);
  if (m) return t('arch2_main_panel_nr', { n: m[1] });
  m = /^Tableau Tertiaire (\d+)R$/.exec(label);
  if (m) return t('arch2_tertiary_panel_nr', { n: m[1] });
  m = /^Tableau Commerce (\d+)R$/.exec(label);
  if (m) return t('arch2_shop_panel_nr', { n: m[1] });
  return label;
}
